import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { client, user } from "@duress/shared";
import {
  createClient,
  createClientContact,
  deleteClient,
  deleteClientContact,
  getClientBySlug,
  getTierThresholds,
  listClientContacts,
  listClients,
  listRecentlyViewedClients,
  listUsers,
  putTierThresholds,
  updateClientContact,
  updateClientCsm,
  updateClientInfo,
  updateClientIndustries,
} from "./api";

const KEY = ["clients"] as const;

export function useClients(): {
  data: client.ClientListItem[] | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: KEY,
    queryFn: listClients,
  });
  return { data, isLoading, error, refetch: () => void refetch() };
}

export function useClientBySlug(slug: string): {
  data: client.ClientListItem | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [...KEY, "slug", slug] as const,
    queryFn: () => getClientBySlug(slug),
    enabled: Boolean(slug),
  });
  return { data, isLoading, error, refetch: () => void refetch() };
}

export function useCreateClient(): {
  mutateAsync: (params: {
    name: string;
    industries: string[];
    region: client.Region;
  }) => Promise<client.ClientListItem>;
  isPending: boolean;
  error: unknown;
} {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: createClient,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending, error: m.error };
}

function tierConfigKey(clientId: string) {
  return ["clients", "tier-config", clientId] as const;
}

export function useTierThresholds(clientId: string): {
  data: client.TierThresholds | undefined;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: tierConfigKey(clientId),
    queryFn: () => getTierThresholds(clientId),
    enabled: Boolean(clientId),
  });
  return { data, isLoading, error };
}

export function usePutTierThresholds(clientId: string): {
  mutateAsync: (thresholds: client.TierThresholds) => Promise<client.TierThresholds>;
  isPending: boolean;
  error: unknown;
} {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (thresholds: client.TierThresholds) =>
      putTierThresholds(clientId, thresholds),
    onSuccess: (saved) => {
      qc.setQueryData(tierConfigKey(clientId), saved);
      // Stored tiers on existing periods reflect the config at upload time,
      // but invalidate periods so any cached client view re-fetches.
      void qc.invalidateQueries({ queryKey: ["periods", clientId] });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending, error: m.error };
}

export function useUpdateClientIndustries(clientId: string): {
  mutateAsync: (industries: string[]) => Promise<client.ClientListItem>;
  isPending: boolean;
  error: unknown;
} {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (industries: string[]) => updateClientIndustries(clientId, industries),
    onSuccess: (updated) => {
      // Update both the list and the slug-keyed cache so detail pages reflect immediately.
      void qc.invalidateQueries({ queryKey: KEY });
      qc.setQueryData([...KEY, "slug", updated.slug] as const, updated);
      // Industry insights aggregate over these tags — invalidate so peer rows refresh.
      void qc.invalidateQueries({ queryKey: ["insights"] });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending, error: m.error };
}

export function useDeleteClient(): {
  mutateAsync: (id: string) => Promise<void>;
  isPending: boolean;
} {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: deleteClient,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending };
}

export function useRecentlyViewedClients(): {
  data: client.ClientListItem[] | undefined;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: [...KEY, "recently-viewed"] as const,
    queryFn: listRecentlyViewedClients,
  });
  return { data, isLoading };
}

export function useClientContacts(clientId: string): {
  data: client.ClientContact[] | undefined;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: ["clients", "contacts", clientId] as const,
    queryFn: () => listClientContacts(clientId),
    enabled: Boolean(clientId),
  });
  return { data, isLoading, error };
}

export function useCreateContact(clientId: string): {
  mutateAsync: (data: client.CreateClientContactRequest) => Promise<client.ClientContact>;
  isPending: boolean;
} {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (data: client.CreateClientContactRequest) =>
      createClientContact(clientId, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["clients", "contacts", clientId] });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending };
}

export function useUpdateContact(clientId: string): {
  mutateAsync: (
    contactId: string,
    data: client.UpdateClientContactRequest,
  ) => Promise<client.ClientContact>;
  isPending: boolean;
} {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: ({
      contactId,
      data,
    }: {
      contactId: string;
      data: client.UpdateClientContactRequest;
    }) => updateClientContact(clientId, contactId, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["clients", "contacts", clientId] });
    },
  });
  return {
    mutateAsync: (contactId, data) => m.mutateAsync({ contactId, data }),
    isPending: m.isPending,
  };
}

export function useDeleteContact(clientId: string): {
  mutateAsync: (contactId: string) => Promise<void>;
  isPending: boolean;
} {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (contactId: string) => deleteClientContact(clientId, contactId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["clients", "contacts", clientId] });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending };
}

export function useUpdateClientInfo(clientId: string): {
  mutateAsync: (info: client.UpdateClientInfoRequest) => Promise<client.ClientListItem>;
  isPending: boolean;
  error: unknown;
} {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (info: client.UpdateClientInfoRequest) => updateClientInfo(clientId, info),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: KEY });
      qc.setQueryData([...KEY, "slug", updated.slug] as const, updated);
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending, error: m.error };
}

export function useUpdateClientCsm(clientId: string): {
  mutateAsync: (userId: string | null) => Promise<client.ClientListItem>;
  isPending: boolean;
  error: unknown;
} {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (userId: string | null) => updateClientCsm(clientId, userId),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: KEY });
      qc.setQueryData([...KEY, "slug", updated.slug] as const, updated);
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending, error: m.error };
}

const USERS_KEY = ["users"] as const;

export function useUsers(): {
  data: user.UserListResponse | undefined;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: USERS_KEY,
    queryFn: listUsers,
    staleTime: 5 * 60_000,
  });
  return { data, isLoading };
}
